import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Bell, Calendar, Tag, AlertCircle, Sparkles, CheckCheck, Inbox, Settings as SettingsIcon, CalendarOff, Star, X, Undo2 } from 'lucide-react';
import axios from 'axios';
import { applyAppBadge } from '../utils/webPush';
import AppBadgeToggle from '../components/AppBadgeToggle';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const getTypeMeta = (t) => ({
  event: { label: t('inbox.typeEvent', 'Event'), icon: Calendar, accent: 'text-blue-600', bg: 'bg-blue-100' },
  offer: { label: t('inbox.typeOffer', 'Offer'), icon: Tag, accent: 'text-emerald-600', bg: 'bg-emerald-100' },
  system: { label: t('inbox.typeSystem', 'System'), icon: AlertCircle, accent: 'text-purple-600', bg: 'bg-purple-100' },
  safety_bulletin: { label: t('inbox.typeSafety', 'Safety'), icon: AlertCircle, accent: 'text-red-600', bg: 'bg-red-100' },
  incident: { label: t('inbox.typeIncident', 'Incident'), icon: AlertCircle, accent: 'text-red-700', bg: 'bg-red-100' },
  community_report: { label: t('inbox.typeCommunityReport', 'Community'), icon: AlertCircle, accent: 'text-orange-600', bg: 'bg-orange-100' },
  business_status: { label: t('inbox.typeStatus', 'Status'), icon: CalendarOff, accent: 'text-amber-600', bg: 'bg-amber-100' },
});

const formatWhen = (iso, t) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return t('inbox.justNow', 'just now');
  if (mins < 60) return t('inbox.minsAgo', '{{n}}m ago', { n: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('inbox.hoursAgo', '{{n}}h ago', { n: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return t('inbox.daysAgo', '{{n}}d ago', { n: days });
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
};

const getSubscriptionId = () => {
  try {
    return localStorage.getItem('find2day_sub_id') || localStorage.getItem('onesignal_sub_id') || localStorage.getItem('subscriptionId') || '';
  } catch {
    return '';
  }
};

const getAuthToken = () => {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  } catch {
    return '';
  }
};

const NotificationInbox = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, unread: 0, sponsoredUnread: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const subId = getSubscriptionId();
  const token = getAuthToken();
  const canFetch = !!subId || !!token;

  const fetchInbox = async () => {
    if (!canFetch) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = subId ? `?subscriptionId=${encodeURIComponent(subId)}` : '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/inbox${params}`, { headers });
      setItems(res.data?.items || []);
      const sum = res.data?.summary || { total: 0, unread: 0, sponsoredUnread: 0 };
      setSummary(sum);
      // Iter 158 — keep the home-screen badge in sync on every inbox load.
      // Covers the cross-device case: a second device reading alerts triggers
      // the next fetch here to clear / lower the badge on this device too.
      applyAppBadge(sum.unread || 0);
    } catch (e) {
      setError(t('inbox.errLoad', 'Could not load notifications'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInbox();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = async (id) => {
    try {
      const params = subId ? `?subscriptionId=${encodeURIComponent(subId)}` : '';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/inbox/${id}/read${params}`, {}, { headers });
    } catch {
      // silent — UI updates optimistically below
    }
  };

  const markAllRead = async () => {
    const params = subId ? `?subscriptionId=${encodeURIComponent(subId)}` : '';
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    try {
      await axios.post(`${API}/inbox/mark-all-read${params}`, {}, { headers });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setSummary((s) => ({ ...s, unread: 0, sponsoredUnread: 0 }));
      // Iter 158 — clear the home-screen badge immediately
      applyAppBadge(0);
    } catch {
      setError(t('inbox.errMarkAll', 'Failed to mark all as read'));
      setTimeout(() => setError(null), 3000);
    }
  };

  // ------ Jul 2026 — pin / dismiss handlers ------
  // Both are optimistic: we mutate local state immediately and revert on error.
  // Dismiss also shows a 5-second Undo toast so accidental swipes are recoverable.
  const [undoToast, setUndoToast] = useState(null); // { id, item, timeoutId }
  const undoRef = useRef(null);

  const authParams = () => (subId ? `?subscriptionId=${encodeURIComponent(subId)}` : '');
  const authHeaders = () => (token ? { Authorization: `Bearer ${token}` } : {});

  const applyPinnedOrder = (list) =>
    [...list].sort((a, b) => {
      // Pinned first, then newest → oldest.
      if (!!b.pinned !== !!a.pinned) return b.pinned ? 1 : -1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const togglePin = async (n) => {
    const nextPinned = !n.pinned;
    setItems((prev) => applyPinnedOrder(prev.map((x) => (x.id === n.id ? { ...x, pinned: nextPinned } : x))));
    try {
      await axios.put(
        `${API}/inbox/${n.id}/${nextPinned ? 'pin' : 'unpin'}${authParams()}`,
        {},
        { headers: authHeaders() },
      );
    } catch {
      // Revert on failure
      setItems((prev) => applyPinnedOrder(prev.map((x) => (x.id === n.id ? { ...x, pinned: !nextPinned } : x))));
      setError(t('inbox.errPin', 'Could not update pinned state'));
      setTimeout(() => setError(null), 3000);
    }
  };

  const clearUndoToast = () => {
    if (undoRef.current) {
      clearTimeout(undoRef.current);
      undoRef.current = null;
    }
    setUndoToast(null);
  };

  const dismissNotification = async (n) => {
    // Optimistic remove + open undo toast
    const wasUnread = !n.read;
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    setSummary((s) => ({
      ...s,
      total: Math.max(0, s.total - 1),
      unread: wasUnread ? Math.max(0, s.unread - 1) : s.unread,
    }));
    if (wasUnread) applyAppBadge(Math.max(0, summary.unread - 1));

    clearUndoToast();
    setUndoToast({ id: n.id, item: n });
    undoRef.current = setTimeout(() => setUndoToast(null), 5000);

    try {
      await axios.put(`${API}/inbox/${n.id}/dismiss${authParams()}`, {}, { headers: authHeaders() });
    } catch {
      // Revert on failure — restore local state, clear toast
      setItems((prev) => applyPinnedOrder([...prev, n]));
      setSummary((s) => ({
        ...s,
        total: s.total + 1,
        unread: wasUnread ? s.unread + 1 : s.unread,
      }));
      clearUndoToast();
      setError(t('inbox.errDismiss', 'Could not dismiss notification'));
      setTimeout(() => setError(null), 3000);
    }
  };

  const undoDismiss = async () => {
    if (!undoToast) return;
    const { item } = undoToast;
    clearUndoToast();
    // Optimistic restore
    setItems((prev) => applyPinnedOrder([...prev, { ...item, dismissed: false }]));
    setSummary((s) => ({
      ...s,
      total: s.total + 1,
      unread: !item.read ? s.unread + 1 : s.unread,
    }));
    try {
      await axios.put(`${API}/inbox/${item.id}/undismiss${authParams()}`, {}, { headers: authHeaders() });
    } catch {
      // If server refuses, force a fresh fetch to resync
      fetchInbox();
    }
  };

  useEffect(() => () => clearUndoToast(), []);

  const openNotification = async (n) => {
    // Optimistic UI + mark read
    if (!n.read) {
      const nextUnread = Math.max(0, summary.unread - 1);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setSummary((s) => ({
        ...s,
        unread: nextUnread,
        sponsoredUnread: n.sponsored ? Math.max(0, s.sponsoredUnread - 1) : s.sponsoredUnread,
      }));
      markRead(n.id);
      // Iter 158 — keep the home-screen badge in sync with the new unread count
      applyAppBadge(nextUnread);
    }
    if (n.url) {
      // Jul 2026 — a PINNED notification may point at content that has since
      // expired. Pass ?includeExpired=1 for safety-bulletin deep links so the
      // detail page can serve the archived version with an "expired for
      // reference" banner. Other route handlers accept unknown params without
      // side effects, so this is safe to always pass on pinned rows.
      let url = n.url;
      if (n.pinned && /^\/safety\//.test(url) && !/[?&]includeExpired=/.test(url)) {
        url += (url.includes('?') ? '&' : '?') + 'includeExpired=1';
      }
      // External absolute URLs go full, internal relative paths use react-router
      if (/^https?:\/\//i.test(url)) {
        window.location.href = url;
      } else {
        navigate(url);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16" data-testid="inbox-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-lg sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            data-testid="inbox-back-btn"
            aria-label={t('common.back', 'Back')}
          >
            <ArrowLeft className="h-5 w-5 rtl-flip" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Bell className="h-5 w-5" /> {t('inbox.title', 'Notifications')}
            </h1>
            {summary.unread > 0 && (
              <p className="text-xs text-purple-100" data-testid="inbox-summary">
                {t('inbox.unreadCount', '{{n}} unread', { n: summary.unread })}{summary.sponsoredUnread > 0 ? ` · ${t('inbox.sponsoredCount', '{{n}} sponsored', { n: summary.sponsoredUnread })}` : ''}
              </p>
            )}
          </div>
          <button
            onClick={() => navigate('/settings')}
            className="p-2 rounded-lg hover:bg-white/20 transition-colors"
            data-testid="inbox-settings-link"
            title={t('inbox.settingsTitle', 'Notification settings')}
            aria-label={t('inbox.settingsTitle', 'Notification settings')}
          >
            <SettingsIcon className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-3">
        {/* Description: tells users what kinds of alerts arrive in this inbox */}
        <p className="text-xs text-gray-600 leading-relaxed bg-purple-50 border border-purple-100 rounded-lg px-3 py-2" data-testid="inbox-description">
          {t('inbox.description', 'Your alerts: businesses you follow, towns you subscribed to, and sponsored deals if you enabled them.')}
        </p>

        {/* App-icon badge / phone-alert opt-in — reachable for anonymous town
            followers (this page is public, unlike /settings). */}
        <AppBadgeToggle />

        {/* Action bar */}
        {summary.unread > 0 && (
          <div className="flex justify-end">
            <button
              onClick={markAllRead}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
              data-testid="inbox-mark-all-read-btn"
            >
              <CheckCheck className="h-4 w-4" /> {t('inbox.markAllRead', 'Mark all as read')}
            </button>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">{error}</div>
        )}

        {/* List */}
        {!canFetch ? (
          <EmptyState
            icon={Inbox}
            title={t('inbox.emptyEnableTitle', 'Enable notifications')}
            body={t('inbox.emptyEnableBody', 'Turn on notifications from a business page or in Settings to start receiving alerts here.')}
            cta={{ label: t('inbox.openSettings', 'Open Settings'), onClick: () => navigate('/settings') }}
          />
        ) : loading ? (
          <div className="p-10 text-center text-gray-400 text-sm">{t('common.loading', 'Loading…')}</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title={t('inbox.emptyTitle', 'No alerts yet')}
            body={t('inbox.emptyBody', 'Follow businesses or towns to start receiving updates.')}
          />
        ) : (
          <ul className="space-y-2" data-testid="inbox-list">
            {items.map((n) => (
              <InboxRow
                key={n.id}
                notification={n}
                onOpen={openNotification}
                onTogglePin={togglePin}
                onDismiss={dismissNotification}
                t={t}
              />
            ))}
          </ul>
        )}
      </main>

      {/* Jul 2026 — Undo dismiss toast (5-second window) */}
      {undoToast && (
        <div
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white rounded-full shadow-lg px-4 py-2.5 flex items-center gap-3 text-sm animate-in fade-in slide-in-from-bottom-2"
          data-testid="inbox-undo-toast"
          role="status"
        >
          <span>{t('inbox.dismissed', 'Notification dismissed')}</span>
          <button
            onClick={undoDismiss}
            className="inline-flex items-center gap-1 font-semibold text-purple-300 hover:text-purple-100 transition-colors"
            data-testid="inbox-undo-btn"
          >
            <Undo2 className="h-4 w-4" /> {t('inbox.undo', 'Undo')}
          </button>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ icon: Icon, title, body, cta }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-10 text-center" data-testid="inbox-empty">
    <Icon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
    <p className="text-gray-700 font-medium">{title}</p>
    <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">{body}</p>
    {cta && (
      <button
        onClick={cta.onClick}
        className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700"
      >
        {cta.label}
      </button>
    )}
  </div>
);

const InboxRow = ({ notification, onOpen, onTogglePin, onDismiss, t }) => {
  const meta = getTypeMeta(t)[notification.type] || getTypeMeta(t).system;
  const Icon = meta.icon;
  const unread = !notification.read;
  const pinned = !!notification.pinned;

  // Sponsored items get a distinct amber card; normal items use subtle unread accent
  const cardStyle = notification.sponsored
    ? 'bg-amber-50 border-amber-200 hover:border-amber-300'
    : pinned
    ? 'bg-purple-50/40 border-purple-200 hover:border-purple-300'
    : unread
    ? 'bg-white border-purple-200 hover:border-purple-300'
    : 'bg-white border-gray-100 hover:border-gray-200';

  // ------ Swipe-left-to-dismiss (touch devices) ------
  // Uses Pointer Events so it also works with a stylus/mouse-drag on desktop.
  // Vertical drags don't trigger; only mostly-horizontal drags past 80 px do.
  const [dragX, setDragX] = React.useState(0);
  const dragState = React.useRef(null);

  const onPointerDown = (e) => {
    // Only on touch/pen — mouse users have the always-visible icon.
    if (e.pointerType === 'mouse') return;
    dragState.current = { startX: e.clientX, startY: e.clientY, locked: null };
  };
  const onPointerMove = (e) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    if (dragState.current.locked === null) {
      // Wait until movement clears the noise floor, then lock the axis.
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      dragState.current.locked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (dragState.current.locked !== 'x') return;
    // Only allow left swipe (dx < 0). Ignore rightward drag.
    if (dx > 0) { setDragX(0); return; }
    setDragX(Math.max(dx, -160));
  };
  const finishDrag = (commit) => {
    dragState.current = null;
    if (commit) {
      // Slide the row off-screen then dismiss so the animation completes.
      setDragX(-480);
      window.setTimeout(() => onDismiss(notification), 180);
    } else {
      setDragX(0);
    }
  };
  const onPointerUp = () => {
    if (!dragState.current && dragX === 0) return;
    const shouldDismiss = dragX <= -80;
    finishDrag(shouldDismiss);
  };
  const onPointerCancel = () => { dragState.current = null; setDragX(0); };

  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };

  return (
    <li className="relative overflow-hidden rounded-xl">
      {/* Red swipe-reveal backdrop (only visible while dragging left). */}
      {dragX < -8 && (
        <div
          className="absolute inset-0 bg-red-500 flex items-center justify-end pr-6 text-white font-semibold rounded-xl"
          aria-hidden="true"
        >
          <div className="flex items-center gap-1.5 text-sm">
            <X className="h-4 w-4" /> {t('inbox.dismiss', 'Dismiss')}
          </div>
        </div>
      )}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        style={{
          transform: `translateX(${dragX}px)`,
          transition: dragState.current ? 'none' : 'transform 180ms ease-out',
          touchAction: 'pan-y',
        }}
      >
        <div
          className={`w-full text-left rounded-xl border shadow-sm p-4 transition-colors ${cardStyle}`}
          data-testid={`inbox-item-${notification.id}`}
        >
          <div className="flex items-start gap-3">
            <button
              onClick={() => onOpen(notification)}
              className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${notification.sponsored ? 'bg-amber-100' : meta.bg}`}
              aria-label={t('inbox.open', 'Open')}
            >
              {notification.sponsored ? (
                <Sparkles className={`h-5 w-5 text-amber-600`} />
              ) : (
                <Icon className={`h-5 w-5 ${meta.accent}`} />
              )}
            </button>
            <button
              onClick={() => onOpen(notification)}
              className="flex-1 min-w-0 text-left"
              data-testid={`inbox-open-${notification.id}`}
            >
              <div className="flex items-center gap-2 flex-wrap mb-1">
                {notification.sponsored ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-500 text-white"
                    data-testid={`inbox-sponsored-pill-${notification.id}`}
                    title={t('inbox.sponsoredTitle', 'This is a promoted offer')}
                  >
                    <Sparkles className="h-3 w-3" /> {t('inbox.sponsored', 'Sponsored')}
                  </span>
                ) : (
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.bg} ${meta.accent}`}>
                    {meta.label}
                  </span>
                )}
                {pinned && (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700"
                    data-testid={`inbox-pinned-pill-${notification.id}`}
                    title={t('inbox.pinnedTitle', 'You saved this notification')}
                  >
                    <Star className="h-3 w-3 fill-purple-600 text-purple-600" /> {t('inbox.pinned', 'Pinned')}
                  </span>
                )}
                {unread && (
                  <span className="inline-block w-2 h-2 bg-purple-500 rounded-full" aria-label={t('inbox.unread', 'Unread')} data-testid={`inbox-unread-dot-${notification.id}`} />
                )}
                <span className="text-[11px] text-gray-400 ml-auto">{formatWhen(notification.createdAt, t)}</span>
              </div>
              <p className={`text-sm font-semibold ${unread ? 'text-gray-900' : 'text-gray-700'}`}>{notification.title || t('inbox.fallbackTitle', 'Notification')}</p>
              {notification.body && (
                <p className={`text-sm mt-0.5 ${unread ? 'text-gray-700' : 'text-gray-500'}`}>{notification.body}</p>
              )}
            </button>

            {/* Always-visible action icons (Jul 2026). Stop event propagation so
                they don't also open the notification. */}
            <div className="flex flex-col items-center gap-1 flex-shrink-0">
              <button
                onClick={(e) => { stop(e); onTogglePin(notification); }}
                className={`p-1.5 rounded-lg transition-colors ${pinned ? 'text-purple-600 hover:bg-purple-100' : 'text-gray-400 hover:text-purple-600 hover:bg-gray-100'}`}
                aria-label={pinned ? t('inbox.unpin', 'Unpin') : t('inbox.pin', 'Pin')}
                title={pinned ? t('inbox.unpin', 'Unpin') : t('inbox.pin', 'Pin')}
                data-testid={`inbox-pin-btn-${notification.id}`}
              >
                <Star className={`h-4 w-4 ${pinned ? 'fill-purple-600' : ''}`} />
              </button>
              <button
                onClick={(e) => { stop(e); onDismiss(notification); }}
                className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                aria-label={t('inbox.dismiss', 'Dismiss')}
                title={t('inbox.dismiss', 'Dismiss')}
                data-testid={`inbox-dismiss-btn-${notification.id}`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </li>
  );
};

export default NotificationInbox;
